import React from 'react';
import PropTypes from 'prop-types';
import {
    ErrorOutline as ErrorOutlineIcon,
    Home as HomeIcon,
    Print as PrintIcon,
} from '@material-ui/icons';
import BreadcrumbLayout from '../../../shared/components/common/breadcrumbLayout';
import { FeedbackState, PrimaryButton, SecondaryButton } from '../../../shared/ui';
import {
    ActionButtonsContainer,
    AddressGroup,
    AlertCard,
    AlertHeader,
    AlertList,
    AlertText,
    AlertTitle,
    BodyText,
    BulletList,
    HeaderArea,
    HeaderTitle,
    KeyLabel,
    KeyValueRow,
    PageContainer,
    PageShell,
    SectionCard,
    SectionTitle,
    SignatureSection
} from '../styles';

const ClientShipmentInstructionsView = ({
    currentLanguage,
    isLoading,
    error,
    estimateNumber,
    clientData,
    invoiceRecipient,
    onPrint,
    onBackToHome
}) => {
    const clientLang = currentLanguage.bepiService.client;
    const sharedLang = currentLanguage.bepiService.shared;

    const breadcrumbItems = [
        { label: sharedLang.shared_breadcrumb_home, path: '/bepi/client' },
        { label: sharedLang.shared_breadcrumb_quotations, path: '/bepi/client/quotations' },
        { label: sharedLang.shared_breadcrumb_shipping }
    ];

    if (isLoading) {
        return (
            <BreadcrumbLayout
                title={clientLang.client_shipment_title}
                breadcrumbItems={breadcrumbItems}
                currentLanguage={currentLanguage}
                showBackButton
            >
                <FeedbackState
                    loading
                    loadingMessage={clientLang.client_shipment_loading}
                />
            </BreadcrumbLayout>
        );
    }

    if (error) {
        return (
            <BreadcrumbLayout
                title={clientLang.client_shipment_title}
                breadcrumbItems={breadcrumbItems}
                currentLanguage={currentLanguage}
                showBackButton
            >
                <FeedbackState error={error} />
            </BreadcrumbLayout>
        );
    }

    return (
        <BreadcrumbLayout
            title={clientLang.client_shipment_title}
            breadcrumbItems={breadcrumbItems}
            currentLanguage={currentLanguage}
            showBackButton
        >
            <PageContainer>
                <PageShell>
                    <HeaderArea>
                        <HeaderTitle>{clientLang.client_shipment_title}</HeaderTitle>
                    </HeaderArea>

                    <BodyText>{clientLang.client_shipment_important_warning}</BodyText>

                    <SectionCard>
                        <SectionTitle>{clientLang.client_shipment_quote_data_title}</SectionTitle>
                        <KeyValueRow>
                            <KeyLabel>{clientLang.client_shipment_estimate_num_label}</KeyLabel>
                            <span>{estimateNumber}</span>
                        </KeyValueRow>
                        <KeyValueRow>
                            <KeyLabel>{clientLang.client_shipment_cnpj_label}</KeyLabel>
                            <span>{clientData.cnpj}</span>
                        </KeyValueRow>
                    </SectionCard>

                    <SectionCard>
                        <SectionTitle>{clientLang.client_shipment_sender_title}</SectionTitle>
                        <KeyValueRow>
                            <KeyLabel>{clientLang.client_shipment_client_label}</KeyLabel>
                            <span>{clientData.nome}</span>
                        </KeyValueRow>
                        <KeyValueRow>
                            <KeyLabel>{sharedLang.shared_cliente_company_label}</KeyLabel>
                            <span>{clientData.empresa}</span>
                        </KeyValueRow>
                        <KeyValueRow>
                            <KeyLabel>{sharedLang.shared_cliente_phone_label}</KeyLabel>
                            <span>{clientData.telefone}</span>
                        </KeyValueRow>
                        <KeyValueRow>
                            <KeyLabel>{sharedLang.shared_cliente_email_label}</KeyLabel>
                            <span>{clientData.email}</span>
                        </KeyValueRow>
                    </SectionCard>

                    <SectionCard>
                        <SectionTitle>{clientLang.client_shipment_invoice_data_title}</SectionTitle>
                        <KeyValueRow>
                            <KeyLabel>{sharedLang.shared_cliente_cnpj_label}</KeyLabel>
                            <span>{invoiceRecipient.cnpj}</span>
                        </KeyValueRow>
                        <KeyValueRow>
                            <KeyLabel>{clientLang.client_shipment_ie_label}</KeyLabel>
                            <span>{invoiceRecipient.ie}</span>
                        </KeyValueRow>
                        <KeyValueRow>
                            <KeyLabel>{clientLang.client_shipment_company_name_label}</KeyLabel>
                            <span>{invoiceRecipient.companyName}</span>
                        </KeyValueRow>
                        <KeyValueRow>
                            <KeyLabel>{clientLang.client_shipment_address_label}</KeyLabel>
                            <AddressGroup>
                                {invoiceRecipient.addressLines.map((line) => (
                                    <span key={line}>{line}</span>
                                ))}
                            </AddressGroup>
                        </KeyValueRow>
                    </SectionCard>

                    <SectionCard>
                        <SectionTitle>{clientLang.client_shipment_procedure_title}</SectionTitle>
                        <BulletList>
                            <li>{clientLang.client_shipment_procedure_step1}</li>
                            <li>{clientLang.client_shipment_procedure_step2}</li>
                            <li>{clientLang.client_shipment_procedure_step3}</li>
                        </BulletList>
                    </SectionCard>

                    <AlertCard $variant='warning'>
                        <AlertText>{clientLang.client_shipment_sedex_warning}</AlertText>
                    </AlertCard>

                    <AlertCard $variant='warning'>
                        <AlertText>{clientLang.client_shipment_certificate_hint}</AlertText>
                    </AlertCard>

                    <AlertCard $variant='danger'>
                        <AlertHeader>
                            <ErrorOutlineIcon fontSize='small' />
                            <AlertTitle $variant='danger'>{clientLang.client_shipment_important_title}</AlertTitle>
                        </AlertHeader>
                        <AlertList>
                            <AlertText>{clientLang.client_shipment_shipping_costs_warning}</AlertText>
                            <AlertText>{clientLang.client_shipment_refusal_right_warning}</AlertText>
                            <AlertText>{clientLang.client_shipment_incorrect_nf_warning}</AlertText>
                        </AlertList>
                    </AlertCard>

                    <SignatureSection>
                        <BodyText>
                            <strong>{invoiceRecipient.companyName}</strong>
                        </BodyText>
                        <BodyText>{clientLang.client_shipment_doubts_help}</BodyText>
                    </SignatureSection>

                    <ActionButtonsContainer>
                        <SecondaryButton
                            type='button'
                            onClick={onBackToHome}
                            startIcon={<HomeIcon />}
                        >
                            {clientLang.client_shipment_home_btn}
                        </SecondaryButton>
                        <PrimaryButton
                            type='button'
                            onClick={onPrint}
                            startIcon={<PrintIcon />}
                        >
                            {clientLang.client_shipment_print_btn}
                        </PrimaryButton>
                    </ActionButtonsContainer>
                </PageShell>
            </PageContainer>
        </BreadcrumbLayout>
    );
};

ClientShipmentInstructionsView.propTypes = {
    currentLanguage: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    estimateNumber: PropTypes.string.isRequired,
    clientData: PropTypes.shape({
        cnpj: PropTypes.string.isRequired,
        nome: PropTypes.string.isRequired,
        empresa: PropTypes.string.isRequired,
        telefone: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
    }).isRequired,
    invoiceRecipient: PropTypes.shape({
        cnpj: PropTypes.string.isRequired,
        ie: PropTypes.string.isRequired,
        companyName: PropTypes.string.isRequired,
        addressLines: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    onPrint: PropTypes.func.isRequired,
    onBackToHome: PropTypes.func.isRequired,
};

ClientShipmentInstructionsView.defaultProps = {
    error: null,
};

export default ClientShipmentInstructionsView;
